import { FC, useEffect, useState } from "react";
import { Button, Form, Input, Modal, Space, Spin, Table, Typography, Upload, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { RcFile } from "antd/lib/upload";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { PlainDarkPanelless } from "survey-core/themes";
import "survey-core/survey-core.css";

import { capitalize } from "../../../common/String";
import {
    Project,
    ProjectType,
    allowedExtensions,
    getProjectTypeLabel,
    hasAllowedExtension,
} from "../../../types/ProjectTypes";
import {
    createProject,
    getProject,
    getInitialSurveySchema,
    submitAnalysis,
} from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { ReactComponent as ContractAnalysisIcon } from "../../../assets/icons/svg/contract-analysis-gradient.svg";
import { ReactComponent as RfxAnalysisIcon } from "../../../assets/icons/svg/rfc_logo.svg";
import styles from "./ModalProjectWizard.module.less";

export const ProjectTypeTitle = {
    [ProjectType.DEAL]: "Analyze contracts",
    [ProjectType.RFX]: "Compare proposals",
};

export const ProjectTypeIcon = {
    [ProjectType.DEAL]: <ContractAnalysisIcon style={{ width: 32, height: 32 }} />,
    [ProjectType.RFX]: <RfxAnalysisIcon style={{ width: 32, height: 32 }} />,
};

export const ProjectTypeFileSuggestions = {
    [ProjectType.DEAL]: (
        <div>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
                Here are some document types to consider uploading.
            </Typography.Text>
            <div
                style={{
                    columnCount: 2,
                    columnGap: "24px",
                    listStylePosition: "outside",
                }}
            >
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li>Core contract documents</li>
                    <li>
                        The signed agreement or draft contract such as Master Services Agreement,
                        Statement of Work, Order Form
                    </li>
                    <li>Pricing schedules, rate cards, or fee exhibits</li>
                    <li>Amendments, addenda, or contract changes</li>
                    <li>Statements of work or scope documents</li>
                    <li>Security, privacy, or data protection addenda</li>
                    <li>Additional documents that can improve the analysis (if available)</li>
                    <li>Usage or volume reports tied to the contract</li>
                    <li>Invoices or billing summaries</li>
                    <li>Renewal notices or prior negotiation correspondence</li>
                    <li>Internal deal summaries or approval documents</li>
                </ul>
            </div>
        </div>
    ),
    [ProjectType.RFX]: (
        <div>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
                Here are some document types to consider uploading.
            </Typography.Text>
            <div
                style={{
                    columnCount: 2,
                    columnGap: "24px",
                    listStylePosition: "outside",
                }}
            >
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li>Vendor proposals from each respondent</li>
                    <li>The original RFX or requirements document</li>
                    <li>Pricing responses or cost breakdowns from vendors</li>
                    <li>Technical responses or solution descriptions</li>
                    <li>Vendor questionnaire responses</li>
                    <li>Security, privacy, or compliance documentation</li>
                    <li>Evaluation scorecards or scoring criteria</li>
                    <li>Reference check notes or vendor assessments</li>
                    <li>Proof of concept or demo results</li>
                    <li>Internal requirements or weighting documents</li>
                </ul>
            </div>
        </div>
    ),
};

export const ProjectTypeSurveySubtitle = {
    [ProjectType.DEAL]:
        "This information helps tailor the analysis and negotiation recommendations.",
    [ProjectType.RFX]: "This information helps tailor the RFX analysis and vendor evaluation.",
};

interface ModalProjectWizardProps {
    open: boolean;
    defaultProjectType: ProjectType;
    onClose: (project?: Project) => void;
    onSaveAsDraft: (project: Project) => void;
    onComplete: (project: Project, analysisVersion: number) => void;
}

export const ModalProjectWizard: FC<ModalProjectWizardProps> = ({
    open,
    defaultProjectType,
    onClose,
    onSaveAsDraft,
    onComplete,
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [projectName, setProjectName] = useState("");
    const [pendingFiles, setPendingFiles] = useState<RcFile[]>([]);
    const [project, setProject] = useState<Project | null>(null);

    // Step 2: Survey
    const [schema, setSchema] = useState<object | undefined>(undefined);
    const [surveyModel, setSurveyModel] = useState<Model | null>(null);

    // Loading states
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingSchema, setIsLoadingSchema] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (open) {
            setCurrentStep(0);
            setProjectName("");
            setPendingFiles([]);
            setProject(null);
            setSchema(undefined);
            setSurveyModel(null);
            setTimeout(() => {
                const input = document.getElementById("wizard-project-name");
                input?.focus();
            }, 300);
        }
    }, [open]);

    // Load survey schema once when entering step 1 for the first time
    useEffect(() => {
        if (currentStep !== 1 || schema) return;

        const fetchSchema = async () => {
            try {
                setIsLoadingSchema(true);
                const surveySchema = (await getInitialSurveySchema(defaultProjectType)).schema;
                setSchema(surveySchema);

                const model = new Model(surveySchema);
                model.applyTheme({
                    ...PlainDarkPanelless,
                    cssVariables: {
                        ...PlainDarkPanelless.cssVariables,
                        "--sjs-base-unit": "6px",
                    },
                });
                model.showCompleteButton = false;

                setSurveyModel(model);
            } catch (error) {
                handleError(error);
            } finally {
                setIsLoadingSchema(false);
            }
        };

        fetchSchema();
    }, [currentStep, schema, defaultProjectType]);

    const handleFileSelect = (file: RcFile) => {
        if (!hasAllowedExtension(file.name)) {
            message.error(
                `Unsupported file type. Please upload one of: ${allowedExtensions.join(", ")}`
            );
            return false;
        }
        setPendingFiles((prev) => [...prev, file]);
        return false; // Prevent auto upload
    };

    const handlePendingFileRemove = (file: RcFile) => {
        setPendingFiles((prev) => prev.filter((f) => f.uid !== file.uid));
    };

    // Create the project with survey and files in a single call
    const saveAll = async () => {
        let currentProject = project;

        if (!currentProject) {
            currentProject = await createProject({
                name: projectName.trim(),
                type: defaultProjectType,
                survey: schema && surveyModel
                    ? { schema, response: surveyModel.data }
                    : undefined,
                files: pendingFiles.length > 0 ? pendingFiles : undefined,
            });
            setPendingFiles([]);
        }

        // Refresh project to get updated state
        const refreshed = await getProject(currentProject.id);
        setProject(refreshed);
        return refreshed;
    };

    const handleStep1Next = () => {
        if (!projectName.trim()) {
            message.error("Please enter a project name.");
            return;
        }
        setCurrentStep(1);
    };

    const handleStep2Next = () => {
        setCurrentStep(2);
    };

    const handleAnalyzeNow = async () => {
        try {
            setIsProcessing(true);
            const saved = await saveAll();
            if (!saved) return;
            const savedAnalysis = saved.analyses[0];
            await submitAnalysis(saved.id);
            onComplete(saved, savedAnalysis.version);
        } catch (error) {
            handleError(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSaveAsDraft = async () => {
        try {
            setIsSaving(true);
            const saved = await saveAll();
            if (!saved) return;
            onSaveAsDraft(saved);
        } catch (error) {
            handleError(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleClose = async () => {
        if (isProcessing || isSaving) return;

        // Only save if the user has entered a name or uploaded files
        const hasContent = projectName.trim() || pendingFiles.length > 0;
        if (hasContent) {
            try {
                setIsSaving(true);
                await saveAll();
            } catch (error) {
                handleError(error);
            } finally {
                setIsSaving(false);
            }
        }

        onClose(project || undefined);
    };

    // Review checks for Step 3
    const reviewChecks = () => {
        const checks: { label: string; passed: boolean }[] = [];

        const totalFiles = pendingFiles.length;
        checks.push({
            label: `Documents uploaded (${totalFiles})`,
            passed: totalFiles > 0,
        });

        if (schema && surveyModel) {
            const validationModel = new Model(schema);
            validationModel.data = surveyModel.data;
            validationModel.validate(false, false, undefined, undefined);
            checks.push({
                label: `${capitalize(getProjectTypeLabel(defaultProjectType))} context survey complete`,
                passed: !validationModel.hasErrors(),
            });
        }

        return checks;
    };

    const allChecksPassed = reviewChecks().every((c) => c.passed);

    // --- Render Steps ---

    const renderSetupStep = () => {
        const fileColumns = [
            {
                title: "File name",
                key: "name",
                render: (_: any, file: RcFile) => file.name,
            },
            {
                title: "",
                key: "actions",
                width: 50,
                render: (_: any, file: RcFile) => (
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => handlePendingFileRemove(file)}
                    />
                ),
            },
        ];

        return (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Form layout="vertical">
                    <Form.Item
                        label="PROJECT NAME"
                        validateStatus={projectName.trim() ? "" : undefined}
                    >
                        <Input
                            id="wizard-project-name"
                            placeholder="Enter project name"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                        />
                    </Form.Item>
                </Form>

                <div>
                    <h3>Documents</h3>
                    <Upload.Dragger
                        multiple
                        beforeUpload={handleFileSelect}
                        fileList={[]}
                        accept={allowedExtensions.join(",")}
                        className={styles.fileDragger}
                        disabled={isProcessing}
                    >
                        <div style={{ textAlign: "center" }}>
                            {ProjectTypeIcon[defaultProjectType]}
                            <div style={{ marginTop: 4 }}>
                                <Typography.Text type="secondary">
                                    Drop or click to add files
                                </Typography.Text>
                            </div>
                        </div>
                    </Upload.Dragger>
                    {pendingFiles.length > 0 && (
                        <Table
                            dataSource={pendingFiles}
                            columns={fileColumns}
                            pagination={false}
                            rowKey="uid"
                            size="small"
                            style={{ marginTop: 12 }}
                        />
                    )}

                    <div style={{ margin: "16px 0" }}>
                        {ProjectTypeFileSuggestions[defaultProjectType]}
                    </div>
                </div>
            </Space>
        );
    };

    const renderDealContextStep = () => {
        if (isLoadingSchema) {
            return (
                <div style={{ textAlign: "center", padding: 48 }}>
                    <Spin />
                </div>
            );
        }

        return (
            <div>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                    Tell us about the {capitalize(getProjectTypeLabel(defaultProjectType))}.
                </div>
                <p>{ProjectTypeSurveySubtitle[defaultProjectType]}</p>
                <div className={styles.surveyContainer}>
                    {surveyModel && <Survey model={surveyModel} />}
                </div>
            </div>
        );
    };

    const renderReviewStep = () => {
        const totalFiles = pendingFiles.length;

        let surveyComplete = true;
        if (schema && surveyModel) {
            const validationModel = new Model(schema);
            validationModel.data = surveyModel.data;
            validationModel.validate(false, false, undefined, undefined);
            surveyComplete = !validationModel.hasErrors();
        }

        return (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Typography.Text>
                    Review the following before generating the playbook:
                </Typography.Text>
                <div className={styles.reviewItem}>
                    {totalFiles > 0 ? (
                        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                    ) : (
                        <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
                    )}
                    <span>
                        {totalFiles} document{totalFiles !== 1 ? "s" : ""} uploaded.{" "}
                        <button type="button" style={{ color: "#1890FF", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }} onClick={() => setCurrentStep(0)}>
                            Upload more documents.
                        </button>
                    </span>
                </div>
                <div className={styles.reviewItem}>
                    {surveyComplete ? (
                        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                    ) : (
                        <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
                    )}
                    <span>
                        {capitalize(getProjectTypeLabel(defaultProjectType))} context survey{" "}
                        {surveyComplete ? "complete" : "incomplete"}.
                        {!surveyComplete && (
                            <button
                                type="button"
                                style={{ marginLeft: 8, color: "#1890FF", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
                                onClick={() => setCurrentStep(1)}
                            >
                                Complete survey
                            </button>
                        )}
                    </span>
                </div>
            </Space>
        );
    };

    const stepContent = [renderSetupStep, renderDealContextStep, renderReviewStep];

    const buttonStyle: React.CSSProperties = {
        border: "none",
        background: "none",
        color: "#1890FF",
        boxShadow: "none",
    };

    const renderFooter = () => {
        const leftButtons: React.ReactNode[] = [];
        const rightButtons: React.ReactNode[] = [];

        // Back button on the left (steps 1 and 2)
        if (currentStep === 1 || currentStep === 2) {
            leftButtons.push(
                <Button key="back" onClick={handleBack} disabled={isProcessing} style={buttonStyle}>
                    Back
                </Button>
            );
        }

        // Close button on the left for step 0
        if (currentStep === 0) {
            leftButtons.push(
                <Button
                    key="close"
                    onClick={handleClose}
                    disabled={isProcessing}
                    style={buttonStyle}
                >
                    Close
                </Button>
            );
        }

        // Next / Analyze Now
        if (currentStep === 0) {
            rightButtons.push(
                <Button
                    key="next"
                    type="text"
                    onClick={handleStep1Next}
                    loading={isProcessing}
                    disabled={!projectName.trim()}
                    style={buttonStyle}
                >
                    Next
                </Button>
            );
        } else if (currentStep === 1) {
            rightButtons.push(
                <Button
                    key="next"
                    type="text"
                    onClick={handleStep2Next}
                    loading={isProcessing}
                    style={buttonStyle}
                >
                    Next
                </Button>
            );
        } else if (currentStep === 2) {
            rightButtons.push(
                <Button
                    key="save-draft"
                    type="text"
                    onClick={handleSaveAsDraft}
                    disabled={isProcessing || isSaving}
                    style={buttonStyle}
                >
                    {isSaving ? "Saving..." : "Save as draft"}
                </Button>
            );
            rightButtons.push(
                <Button
                    key="analyze"
                    type="text"
                    onClick={handleAnalyzeNow}
                    disabled={!allChecksPassed || isProcessing}
                    style={buttonStyle}
                >
                    {isProcessing ? "Submitting..." : "Generate playbook"}
                </Button>
            );
        }

        return (
            <div className={styles.footerInner}>
                <Space>{leftButtons}</Space>
                <Space>{rightButtons}</Space>
            </div>
        );
    };

    return (
        <Modal
            title={
                <span>
                    {ProjectTypeTitle[defaultProjectType]}
                    {isSaving && (
                        <span
                            style={{
                                marginLeft: 12,
                                color: "#FF981F",
                                fontSize: 14,
                                fontWeight: 400,
                            }}
                        >
                            Saving...
                        </span>
                    )}
                </span>
            }
            open={open}
            onCancel={handleClose}
            footer={null}
            width={1000}
            destroyOnClose
            maskClosable={false}
            className={styles.wizardModal}
            centered={false}
            style={{ top: 64 }}
            maskStyle={{ background: "#222937", top: 64 }}
        >
            <div className={styles.stepsBar}>
                {[
                    "Project setup",
                    `${capitalize(getProjectTypeLabel(defaultProjectType))} context`,
                    "Review",
                ].map((label, i) => (
                    <span
                        key={i}
                        className={
                            i < currentStep
                                ? styles.stepComplete
                                : i === currentStep
                                  ? styles.stepCurrent
                                  : styles.step
                        }
                    >
                        {i + 1}. {label}
                    </span>
                ))}
            </div>
            <div className={styles.stepContent}>{stepContent[currentStep]()}</div>
            <div className={styles.footerButtons}>{renderFooter()}</div>
        </Modal>
    );
};
